import { Box, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

export const TermsPage = () => {
  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="md">
        <Typography
          variant="h1"
          sx={{
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
          }}
        >
          Terms of Use
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Introduction */}
          <Box>
            <Typography variant="body1">
              Welcome to the Calorium app! Please read this Terms of Use
              agreement ("Agreement") carefully. By using the app and its
              services (collectively, the "Service"), you agree to be bound by
              the terms of this document.
            </Typography>
          </Box>

          {/* Acceptance of Terms */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1">
              By accessing or using the app, you agree to comply with this
              Agreement. If you do not agree, please do not use the Service.
            </Typography>
          </Box>

          {/* Registration */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              2. Registration
            </Typography>
            <Typography variant="body1">
              To access certain features, you may be required to create an
              account and provide a valid email address and password.
            </Typography>
          </Box>

          {/* Privacy Policy */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              3. Privacy Policy
            </Typography>
            <Typography variant="body1">
              Your use of the Service is governed by our{' '}
              <Box
                component={Link}
                to="/privacy"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Privacy Policy
              </Box>
              . We recommend reviewing it regularly, as it may be updated from
              time to time.
            </Typography>
          </Box>

          {/* Intellectual Property Rights */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              4. Intellectual Property Rights
            </Typography>
            <Typography variant="body1">
              All intellectual property rights related to the app, its content,
              and its services (excluding user-generated content) belong to the
              individual developer, Tarlan Khakimzade, or its licensors.
            </Typography>
          </Box>

          {/* Subscription and Auto-Renewal */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              5. Subscription and Auto-Renewal
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Certain features of Calorium are available only through a
              subscription ("Calorium Pro"). Subscriptions are managed and
              billed through the App Store using your Apple ID account. Your
              subscription will automatically renew unless canceled at least 24
              hours before the end of the current period.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your account will be charged for renewal within 24 hours prior to
              the end of the current subscription period. You can manage your
              subscription and disable auto-renewal at any time in your Apple ID
              account settings.
            </Typography>
            <Typography variant="body1">
              <strong>Note:</strong> Payments are non-refundable once a
              subscription period begins, even if you stop using the app before
              the period ends.
            </Typography>
          </Box>

          {/* Changes to Terms */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              6. Changes to Terms
            </Typography>
            <Typography variant="body1">
              I may update this Agreement from time to time. You will be
              notified of any changes through the app or via other appropriate
              means. Continued use of the app after any update constitutes your
              acceptance of the revised terms.
            </Typography>
          </Box>

          {/* Termination of Access */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              7. Termination of Access
            </Typography>
            <Typography variant="body1">
              I reserve the right to suspend or terminate your access to the
              Service at any time, without notice, for any reason.
            </Typography>
          </Box>

          {/* Medical Disclaimer */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              8. Medical Disclaimer
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Calorium is not a medical application and does not provide medical
              advice, diagnoses, or treatment plans. All features — including
              calorie tracking, goal setting, and nutritional analysis — are
              intended for informational and educational purposes only.
            </Typography>
            <Typography variant="body1">
              You are solely responsible for any dietary or health-related
              decisions you make. The app provides recommendations based on the
              data you enter (such as weight, goals, and timeline) and does not
              account for personal medical conditions or needs. Always consult
              your doctor or a qualified healthcare provider before starting any
              diet, exercise program, or lifestyle change. I am not liable for
              any outcomes resulting from the use of Calorium — you use the app
              at your own risk.
            </Typography>
          </Box>

          {/* Contact Information */}
          <Box>
            <Typography variant="h2" sx={{ mb: 2 }}>
              9. Contact Information
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              If you have any questions about this Agreement, please contact me:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Email:{' '}
                <Box
                  component="a"
                  href="mailto:x.tarlan@yandex.ru"
                  sx={{ color: 'primary.main', textDecoration: 'none' }}
                >
                  x.tarlan@yandex.ru
                </Box>
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Website:{' '}
                <Box
                  component="a"
                  href="https://calorium.app"
                  sx={{ color: 'primary.main', textDecoration: 'none' }}
                >
                  https://calorium.app
                </Box>
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Address: Azerbaijan, Baku, Moscow Avenue, 48
              </Typography>
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              mt: 4,
              pt: 4,
              borderTop: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © 2025 Calorium by Tarlan Khakimzade
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
